import React from 'react';
import styled from 'styled-components';

import { seriesInkFor, tokens } from 'containers/Intelligence/theme';

import { formatSharePercent, sharePercent } from '../formatMetric';
import { visuallyHidden } from '../visuallyHidden';
import { BandReadout, BandTooltipSlot, bandTooltipPosition } from './BandTooltip';
import { useCaptionFits } from './captionFit';

/** 26 is the Overwatch Execution Workload Share bar (PLA-9674, 5726:6767). */
export type SegmentedDistributionBarHeight = 26 | 30 | 34 | 40 | 58;

export interface IDistributionSegment {
  label: string;
  /** Share of the whole as a 0–1 fraction, the same scale as `LegendTable.pct`. */
  pct: number;
  /** A `tokens.color` value — the colour the paired legend gives this category. */
  color: string;
}

export interface ISegmentedDistributionBarProps {
  segments: IDistributionSegment[];
  /**
   * Bands carry their own label painted on the fill. Pass `false` when the design puts the labels
   * in a paired `LegendTable` instead — they stay in the accessibility tree either way.
   */
  showInlineLabels?: boolean;
  height?: SegmentedDistributionBarHeight;
  /** Rounds the track's ends. Opt-in — see `ROUNDED_BAR_RADIUS`. */
  rounded?: boolean;
  /** Corner radius in px, taking precedence over `rounded`. PLA-9674 draws its bar at r 6. */
  radius?: number;
  /**
   * A band's hover readout, shown above it while the band is hovered or focused, on every band.
   * Without it, a band whose caption is not painted on its fill shows its name and share instead.
   */
  renderTooltip?: (segment: IDistributionSegment) => React.ReactNode;
  className?: string;
}

const DEFAULT_HEIGHT: SegmentedDistributionBarHeight = 58;

/**
 * Square. The r=11 mask this used to carry was read off a preview rather than the file: the render
 * of 589:27692 ends the sentiment bar's Negative band and the CLV bars' Very High band on sharp
 * 90-degree corners, at both ends of every bar the frame draws.
 */
const BAR_RADIUS = '0';

/* PLA-8815: `2533:1670` rounds its band bars — the user reads `DATA-UTILIZATION BANDS` on
   `2558:3932` as a rounded corner. That is a DIFFERENT frame from the one the square default was
   measured on, so it arrives as an opt-in rather than a change of default: the sentiment and CLV
   bars of `589:27692` keep their sharp 90-degree corners.
   The step is `radius.md`. The mask this component used to carry read r=11, which is off-scale and
   was in any case read off a preview; 12 is the nearest step to it and the value is a CHOICE, not a
   measurement of `2558:3932` — flagged for the designer. */
const ROUNDED_BAR_RADIUS = tokens.radius.md;

/**
 * Share-of-bar floor for painting the caption on the fill, used only until the bar is laid out
 * (and under jsdom, where it never is). Once it is, a caption is painted exactly when its own text
 * fits its band (`useCaptionFits`), whatever the band's share.
 */
const INLINE_LABEL_MIN_PERCENT = 12;

const Bar = styled.div<{
  $height: SegmentedDistributionBarHeight;
  $rounded: boolean;
  $radius?: number;
}>`
  display: flex;
  width: 100%;
  height: ${({ $height }) => $height}px;
  border-radius: ${({ $rounded, $radius }) =>
    $radius !== undefined ? `${$radius}px` : $rounded ? ROUNDED_BAR_RADIUS : BAR_RADIUS};
  /* Bands are painted at their own measured share, so shares that do not sum to 1 leave the
     unmeasured remainder showing as track rather than being absorbed into the bands. */
  background: ${tokens.color['track-bg']};
  overflow: hidden;
`;

/* Every bar gets this host: a band whose caption is not painted shows it in a readout above the
   band, and the bar clips its own overflow, so the readout sits outside it. Full width, as the bar
   itself is, so the host takes the bar's place in any layout. */
const TooltipHost = styled.div`
  position: relative;
  width: 100%;
`;

const Segment = styled.span`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  overflow: hidden;
`;

const SegmentLabel = styled.span<{ $inline: boolean; $color: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: ${({ $color }) => $color};
  font-size: ${tokens.type['label-size']};
  font-weight: ${tokens.type['card-title-weight']};
  line-height: ${tokens.type['label-line']};
  white-space: nowrap;

  /* Hidden bands keep their text for assistive tech: without it the bar is an unlabelled block. The
     sighted reader gets it from the band's hover readout. */
  ${({ $inline }) => ($inline ? '' : visuallyHidden)}
`;

/**
 * The screens stack the band name over its share as two lines, the second held back to 88% so the
 * name leads. Only the second line dims: an opacity on the whole caption would cost contrast the
 * ink was chosen for.
 */
const CaptionShare = styled.span`
  opacity: 0.88;
  font-weight: ${tokens.type['body-weight']};
`;

const shareOf = (segment: IDistributionSegment): number =>
  Number.isFinite(segment.pct) && segment.pct > 0 ? segment.pct : 0;

const SegmentedDistributionBar: React.FC<ISegmentedDistributionBarProps> = ({
  segments,
  showInlineLabels = true,
  height = DEFAULT_HEIGHT,
  rounded = false,
  radius,
  renderTooltip,
  className
}) => {
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);
  const barRef = React.useRef<HTMLDivElement>(null);
  const bandRefs = React.useRef<Record<number, HTMLElement | null>>({});
  const captionRefs = React.useRef<Record<number, HTMLElement | null>>({});
  const bands = Array.isArray(segments) ? segments : [];
  const total = bands.reduce((sum, segment) => sum + shareOf(segment), 0);
  const fits = useCaptionFits(
    barRef,
    bandRefs,
    captionRefs,
    `${showInlineLabels}|${bands.map(segment => `${segment.label}:${shareOf(segment)}`).join('|')}`
  );

  // Nothing measured means nothing to split: a full-width band of one colour would read as a
  // 100% population. The card shows its Insufficient Data state instead.
  if (total <= 0) {
    return null;
  }

  // Each band's left edge as a share of the bar, so a readout can sit over its band.
  const starts = bands.reduce(
    (edges: number[], segment, index) => [...edges, edges[index] + sharePercent(shareOf(segment))],
    [0]
  );
  /* Every band carries its caption, accent-purple too, although `on-accent` reads 4.2:1 against
     it, under AA's 4.5:1 at this size: a product decision, so every bar reads the same way. */
  const painted = bands.map(
    (segment, index) =>
      showInlineLabels &&
      (fits?.[index] ?? sharePercent(shareOf(segment)) >= INLINE_LABEL_MIN_PERCENT)
  );
  const hasReadout = (index: number): boolean => Boolean(renderTooltip) || !painted[index];
  const hoveredBand = hoveredIndex === null ? undefined : bands[hoveredIndex];

  return (
    <TooltipHost>
      <Bar
        ref={barRef}
        $height={height}
        $rounded={rounded}
        $radius={radius}
        className={className}
        data-height={height}
        data-inline-labels={showInlineLabels}
        data-rounded={rounded}
        data-testid="segmented-distribution-bar"
      >
        {bands.map((segment, index) => {
          const share = shareOf(segment);

          if (share === 0) {
            return null;
          }

          // A band is painted at exactly the share it is labelled with, so the geometry can never
          // contradict its own caption or the percentage the paired LegendTable prints for the same
          // category. Shares that overshoot 1 are absorbed by flex shrink, not by relabelling.
          const width = sharePercent(share);
          const inline = painted[index];

          return (
            <Segment
              key={index}
              ref={node => {
                bandRefs.current[index] = node;
              }}
              style={{ width: `${width}%`, background: segment.color }}
              data-color={segment.color}
              data-readout={hasReadout(index)}
              data-testid="segmented-distribution-bar-segment"
              {...(renderTooltip
                ? {
                    tabIndex: 0,
                    onMouseEnter: () => setHoveredIndex(index),
                    onMouseLeave: () => setHoveredIndex(null),
                    onFocus: () => setHoveredIndex(index),
                    onBlur: () => setHoveredIndex(null)
                  }
                : hasReadout(index)
                ? {
                    onMouseEnter: () => setHoveredIndex(index),
                    onMouseLeave: () => setHoveredIndex(null)
                  }
                : {})}
            >
              <SegmentLabel
                ref={node => {
                  captionRefs.current[index] = node;
                }}
                $inline={inline}
                $color={seriesInkFor(segment.color, tokens.color['on-accent'])}
                data-inline={inline}
                data-testid="segmented-distribution-bar-label"
              >
                <span data-testid="segmented-distribution-bar-caption-name">{segment.label}</span>
                <CaptionShare data-testid="segmented-distribution-bar-caption-share">
                  {formatSharePercent(share)}
                </CaptionShare>
              </SegmentLabel>
            </Segment>
          );
        })}
      </Bar>
      {hoveredBand && hoveredIndex !== null && hasReadout(hoveredIndex) ? (
        <BandTooltipSlot
          role="tooltip"
          style={bandTooltipPosition(starts[hoveredIndex], sharePercent(shareOf(hoveredBand)))}
          data-testid="segmented-distribution-bar-tooltip"
        >
          {renderTooltip ? (
            renderTooltip(hoveredBand)
          ) : (
            <BandReadout
              label={hoveredBand.label}
              share={formatSharePercent(shareOf(hoveredBand))}
            />
          )}
        </BandTooltipSlot>
      ) : null}
    </TooltipHost>
  );
};

export default SegmentedDistributionBar;
